// Admin Support page (Story #4613, Stream 8c) — route /admin/support.
//   • ?ticket={id} → admin ticket detail + triage (mockup 05)
//   • (default)    → the support queue (mockup 04)
// The route is already gated to innovateflow-admin (App.tsx AdminGuard); the
// release-quarantine control is additionally gated to innovateflow-security-admin
// (read from the same Cognito groups the app already uses).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import PageHeader from '../components/ui/PageHeader'
import { useAuth } from '../contexts/AuthContext'
import { adminListTickets } from '../services/support'
import { SUPPORT_PORTAL } from '../constants/support'
import type { AdminSupportTicket } from '../types/support'
import AdminQueueTable from '../components/support/admin/AdminQueueTable'
import AdminTicketDetail from '../components/support/admin/AdminTicketDetail'

export default function AdminSupport() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const ticketId = params.get('ticket')

  const [tickets, setTickets] = useState<AdminSupportTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isSecurityAdmin = useMemo(
    () => (user?.groups ?? []).includes('innovateflow-security-admin'),
    [user?.groups],
  )

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    // Fetch both open and closed so the "Resolved / Everything" filters have data
    // to work with (the backend queue-index is keyed status#portal).
    Promise.all([
      adminListTickets({ portal: SUPPORT_PORTAL, status: 'open' }),
      adminListTickets({ portal: SUPPORT_PORTAL, status: 'closed' }),
    ])
      .then(([open, closed]) => {
        setTickets([...(open.tickets ?? []), ...(closed.tickets ?? [])])
        setLoading(false)
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Could not load the support queue')
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (!ticketId) load()
  }, [ticketId, load])

  const openTicket = useCallback((id: string) => setParams({ ticket: id }), [setParams])
  const backToQueue = useCallback(() => setParams({}), [setParams])

  if (ticketId) {
    return (
      <div className="content-bg">
        <AdminTicketDetail ticketId={ticketId} isSecurityAdmin={isSecurityAdmin} onBack={backToQueue} />
      </div>
    )
  }

  return (
    <div className="content-bg">
      <PageHeader title="Support queue" description="Triage, reply to, and route support tickets across the portal." />
      {loading ? (
        <div className="flex items-center gap-2 py-10 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Loading the support queue…
        </div>
      ) : error ? (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error}</p>
      ) : (
        <AdminQueueTable tickets={tickets} onOpen={openTicket} />
      )}
    </div>
  )
}
